<template>
  <div>
    <CardTable
      :title="title"
      :subTitle="subTitle"
      :columns="table.columns"
      :rows="table.rows"
      :isLoading="table.isLoading"
      :selectable="false"
      :pageSize="serverParams.pageSize"
      :pageNumber="serverParams.pageNumber"
      :totalPage="table.totalPage"
      :totalRecords="table.totalRecords"
      searchText="Cari nama member"
      @onPerPageChange="onPerPageChange"
      @onPageChange="onPageChange"
      @onSearch="onSearch"
    >
      <template #toolbar>
        <b-button variant="light-primary" @click="getAll">
          <i class="fas fa-sync-alt" /> Refresh
        </b-button>
      </template>
      <template #info>
        <treeselect
          class="d-inline-block mr-3 mb-2 text-left"
          style="width: 220px"
          placeholder="Semua Status"
          v-model="serverParams.status"
          :options="statusOptions"
          @input="onFilter"
        />
      </template>
      <template #cell(verification)="data">
        <b-badge
          :variant="data.item.emailVerified ? 'success' : 'secondary'"
          class="mr-1"
        >
          Email
        </b-badge>
        <b-badge :variant="data.item.phoneVerified ? 'success' : 'secondary'">
          WA
        </b-badge>
      </template>
      <template #cell(card)="data">
        <b-badge v-if="data.item.hasCard" variant="success">
          {{ data.item.cardNumber || "Terpasang" }}
        </b-badge>
        <b-badge v-else variant="secondary">Belum ada</b-badge>
      </template>
      <template #cell(status)="data">
        <b-badge :variant="data.item.activated ? 'success' : 'warning'">
          {{ data.item.activated ? "Aktif" : "Belum Aktif" }}
        </b-badge>
        <b-badge v-if="data.item.isCoach" variant="info" class="ml-1">
          Coach
        </b-badge>
      </template>
      <template #cell(registeredAt)="data">
        {{ dateFormat(data.value) }}
      </template>
      <template #cell(action)="data">
        <div class="d-flex justify-content-center flex-wrap" style="gap: 6px">
          <!-- Tiga jalur aktivasi. Member baru lewat kartu + biaya pendaftaran;
               member existing & coach cukup ditautkan ke datanya (tanpa kartu). -->
          <b-dropdown
            v-if="!data.item.activated && !data.item.hasCard"
            size="sm"
            variant="primary"
            text="Aktivasi"
            right
          >
            <b-dropdown-item @click="openAssignCard(data.item)">
              <i class="fas fa-id-card mr-2"></i>
              Member Baru — Assign Kartu
            </b-dropdown-item>
            <b-dropdown-item @click="openLinkMember(data.item)">
              <i class="fas fa-user-friends mr-2"></i>
              Member Existing — Tautkan Data
            </b-dropdown-item>
            <b-dropdown-item @click="openLinkCoach(data.item)">
              <i class="fas fa-dumbbell mr-2"></i>
              Coach — Tautkan Data
            </b-dropdown-item>
          </b-dropdown>
          <!-- Langkah 2: aktivasi (buat transaksi pendaftaran) — perlu kartu dulu. -->
          <b-button
            v-if="!data.item.activated && data.item.hasCard"
            size="sm"
            variant="success"
            :disabled="converting"
            @click="activate(data.item)"
          >
            <i class="fas fa-user-check mr-1"></i>
            Aktivasi
          </b-button>
          <span v-if="data.item.activated" class="text-muted">Sudah aktif</span>
        </div>
      </template>
      <template #empty>
        <EmptyTable
          title="Belum ada member dari aplikasi"
          :description="subTitle"
        />
      </template>
    </CardTable>

    <b-modal
      v-model="modalCard.show"
      title="Assign Kartu Member"
      :no-close-on-backdrop="true"
      ok-title="Simpan Kartu"
      cancel-title="Batal"
      :ok-disabled="modalCard.saving || !isCardValid"
      @ok.prevent="submitAssignCard"
    >
      <p v-if="modalCard.item" class="mb-3">
        Pasang kartu akses untuk
        <b>{{ modalCard.item.fullName }}</b
        >. Member akan diadopsi ke store ini agar bisa dibuatkan transaksi
        pendaftaran.
      </p>
      <b-form-group label="Nama di Kartu">
        <b-form-input
          v-model="modalCard.cardName"
          maxlength="15"
          placeholder="Nama yang dicetak di kartu"
        />
      </b-form-group>
      <b-form-group label="RFID">
        <b-form-input
          v-model="modalCard.rfidNumber"
          type="password"
          autocomplete="off"
          placeholder="Tempelkan / scan kartu RFID"
          @focus="$event.target.select()"
          @input="getCardByRfid"
        />
        <small v-if="modalCard.lookupError" class="text-danger">
          {{ modalCard.lookupError }}
        </small>
      </b-form-group>
      <b-form-group label="Nomor Kartu">
        <b-form-input
          v-model="modalCard.cardNumber"
          disabled
          placeholder="Terisi otomatis dari kartu"
        />
      </b-form-group>
    </b-modal>

    <b-modal
      v-model="modalMember.show"
      title="Aktivasi sebagai Member Existing"
      :no-close-on-backdrop="true"
      ok-title="Tautkan"
      cancel-title="Batal"
      :ok-disabled="modalMember.saving || modalMember.memberId == null"
      @ok.prevent="submitLinkMember"
    >
      <p v-if="modalMember.item" class="mb-4">
        Tautkan akun aplikasi <b>{{ modalMember.item.fullName }}</b> ({{
          modalMember.item.email
        }}) ke data member yang sudah ada. Member tidak perlu kartu baru maupun
        biaya pendaftaran.
      </p>
      <b-form-group label="Pilih Member">
        <!-- v-if agar treeselect dipasang saat modal dibuka (b-modal tidak lazy):
             kandidat awal baru diambil ketika benar-benar dibutuhkan. -->
        <treeselect
          v-if="modalMember.show"
          v-model="modalMember.memberId"
          :async="true"
          :load-options="searchMemberCandidate"
          :default-options="true"
          placeholder="Ketik nama / no. anggota / no. HP member"
        />
      </b-form-group>
      <b-alert show variant="warning" class="mb-0">
        <small>
          Email member yang dipilih akan diganti menjadi
          <b>{{ modalMember.item && modalMember.item.email }}</b> dan akun
          aplikasi ini digabungkan ke data member tersebut — kartu, membership,
          dan riwayat transaksinya tetap utuh. Minta member masuk ulang di
          aplikasi setelah proses selesai.
        </small>
      </b-alert>
    </b-modal>

    <b-modal
      v-model="modalCoach.show"
      title="Aktivasi sebagai Coach"
      :no-close-on-backdrop="true"
      ok-title="Tautkan"
      cancel-title="Batal"
      :ok-disabled="modalCoach.saving || modalCoach.coachIds.length === 0"
      @ok.prevent="submitLinkCoach"
    >
      <p v-if="modalCoach.item" class="mb-4">
        Tautkan akun aplikasi <b>{{ modalCoach.item.fullName }}</b> ({{
          modalCoach.item.email
        }}) ke data coach. Coach tidak memerlukan kartu member.
      </p>
      <b-form-group
        label="Pilih Data Coach"
        description="Coach yang mengajar di lebih dari satu store punya data terpisah per store — pilih semuanya agar aplikasi mengenali seluruh store tempat ia mengajar."
      >
        <treeselect
          v-if="modalCoach.show"
          v-model="modalCoach.coachIds"
          :multiple="true"
          :async="true"
          :load-options="searchCoachCandidate"
          :default-options="true"
          placeholder="Ketik nama / no. coach"
        />
      </b-form-group>
      <b-alert show variant="warning" class="mb-0">
        <small>
          Email data coach yang dipilih akan diganti menjadi
          <b>{{ modalCoach.item && modalCoach.item.email }}</b> — kecocokan
          email inilah yang membuat aplikasi mengenali akun ini sebagai coach.
        </small>
      </b-alert>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ASYNC_SEARCH } from "@riophae/vue-treeselect";
import { appMember as columns } from "@/core/datasource/column";
import { pageSize, dateFormat } from "@/core/utils";
import { SET_MEMBER, SET_CART } from "@/core/services/store/pos.module.js";

export default {
  data: () => ({
    title: "Member Mobile Apps",
    subTitle:
      "Member yang registrasi lewat Mobile Apps — assign kartu lalu buat transaksi pendaftaran di kasir untuk mengaktivasi akunnya",
    serverParams: {
      pageNumber: 1,
      pageSize,
      // Default menampilkan yang belum aktif — kandidat aktivasi di kasir.
      status: "BELUM",
      fullName: null,
    },
    statusOptions: [
      { id: "BELUM", label: "Belum Aktif" },
      { id: "AKTIF", label: "Sudah Aktif" },
    ],
    table: {
      isLoading: false,
      columns,
      rows: [],
      totalPage: 0,
      totalRecords: 0,
    },
    converting: false,
    modalCard: {
      show: false,
      item: null,
      cardName: null,
      rfidNumber: null,
      cardNumber: null,
      lookupError: null,
      lookupSeq: 0,
      saving: false,
    },
    // Aktivasi member existing: gabungkan akun app ke record member lama.
    modalMember: {
      show: false,
      item: null,
      memberId: null,
      saving: false,
    },
    // Aktivasi coach: tautkan akun app ke satu/lebih baris coach (1 baris per store).
    modalCoach: {
      show: false,
      item: null,
      coachIds: [],
      saving: false,
    },
  }),
  computed: {
    ...mapGetters(["store"]),
    isCardValid() {
      const m = this.modalCard;
      return (
        m.rfidNumber != null &&
        m.rfidNumber.length >= 8 &&
        m.cardNumber != null &&
        m.cardNumber.length > 0
      );
    },
  },
  created() {
    this.getAll();
  },
  methods: {
    dateFormat,
    getAll() {
      const self = this;

      const params = {
        pageNumber: self.serverParams.pageNumber,
        pageSize: self.serverParams.pageSize,
        fullName: self.serverParams.fullName,
      };
      // "Semua Status" (null) → filter aktivasi tidak dikirim ke backend.
      if (self.serverParams.status === "BELUM") params.activated = false;
      else if (self.serverParams.status === "AKTIF") params.activated = true;

      self.table.isLoading = true;
      self.$store
        .dispatch("apis/get", {
          url: `/apps-member`,
          params,
        })
        .then((response) => {
          if (response.error) {
            self.$message.error({ zIndex: 10000, message: response.message });
          } else {
            self.table.rows = response.data.data;
            self.table.totalPage = response.data.totalPage;
            self.table.totalRecords = response.data.totalData;
          }
        })
        .finally(() => (self.table.isLoading = false));
    },
    // --- Langkah 1: assign kartu member ---
    openAssignCard(item) {
      this.modalCard.item = item;
      this.modalCard.cardName = (item.fullName || "").substring(0, 15);
      this.modalCard.rfidNumber = null;
      this.modalCard.cardNumber = null;
      this.modalCard.lookupError = null;
      this.modalCard.saving = false;
      this.modalCard.show = true;
    },
    // Scan RFID → cari kartu tersedia di store ini (endpoint sama dgn registrasi member).
    getCardByRfid() {
      const self = this;
      const rfid = self.modalCard.rfidNumber;
      self.modalCard.lookupError = null;
      self.modalCard.cardNumber = null;
      if (self.store == null || rfid == null || rfid.length < 8) return;

      const seq = ++self.modalCard.lookupSeq;
      self.$store
        .dispatch("apis/get", {
          url: `/store/${self.store.id}/card/${rfid}`,
        })
        .then((response) => {
          if (seq !== self.modalCard.lookupSeq) return; // hasil usang
          if (response.error) {
            self.modalCard.lookupError =
              "Kartu tidak ditemukan atau sudah tidak tersedia";
          } else {
            self.modalCard.rfidNumber = response.data.rfidNumber;
            self.modalCard.cardNumber = response.data.cardNumber;
          }
        });
    },
    submitAssignCard() {
      const self = this;
      if (
        self.store == null ||
        self.modalCard.item == null ||
        !self.isCardValid
      )
        return;

      self.modalCard.saving = true;
      self.$store
        .dispatch("apis/post", {
          url: `/store/${self.store.id}/apps-member/${self.modalCard.item.id}/assign-card`,
          params: {
            cardName: self.modalCard.cardName,
            rfidNumber: self.modalCard.rfidNumber,
            cardNumber: self.modalCard.cardNumber,
          },
        })
        .then((response) => {
          if (response.error) {
            self.$message.error({ zIndex: 10000, message: response.message });
          } else {
            self.$message.success({ zIndex: 10000, message: response.message });
            self.modalCard.show = false;
            self.getAll();
          }
        })
        .finally(() => (self.modalCard.saving = false));
    },
    // --- Aktivasi sebagai member existing ---
    // Akun app digabungkan ke record member lama (kartu & riwayatnya tetap),
    // jadi tidak perlu kartu baru maupun biaya pendaftaran.
    openLinkMember(item) {
      this.modalMember.item = item;
      this.modalMember.memberId = null;
      this.modalMember.saving = false;
      this.modalMember.show = true;
    },
    memberCandidateOption(x) {
      const parts = [x.membershipNumber, x.storeName].filter(Boolean);
      return {
        id: x.id,
        label: parts.length
          ? `${x.fullName} (${parts.join(" — ")})`
          : x.fullName,
      };
    },
    searchMemberCandidate({ action, searchQuery, callback }) {
      const self = this;
      if (action !== ASYNC_SEARCH) return;

      self.$store
        .dispatch("apis/get", {
          url: `/apps-member/member-candidate`,
          params: { search: searchQuery, pageNumber: 1, pageSize: 20 },
        })
        .then((response) => {
          if (response.error) callback(new Error(response.message));
          else
            callback(null, response.data.data.map(self.memberCandidateOption));
        });
    },
    submitLinkMember() {
      const self = this;
      if (self.modalMember.item == null || self.modalMember.memberId == null)
        return;

      self.modalMember.saving = true;
      self.$store
        .dispatch("apis/post", {
          url: `/apps-member/${self.modalMember.item.id}/link-member`,
          params: { memberId: self.modalMember.memberId },
        })
        .then((response) => {
          if (response.error) {
            self.$message.error({ zIndex: 10000, message: response.message });
          } else {
            self.$message.success({ zIndex: 10000, message: response.message });
            self.modalMember.show = false;
            self.getAll();
          }
        })
        .finally(() => (self.modalMember.saving = false));
    },
    // --- Aktivasi sebagai coach ---
    // Aplikasi mengenali coach lewat kecocokan email, jadi penautan = menyetel
    // email baris coach terpilih menjadi email akun app.
    openLinkCoach(item) {
      this.modalCoach.item = item;
      this.modalCoach.coachIds = [];
      this.modalCoach.saving = false;
      this.modalCoach.show = true;
    },
    coachCandidateOption(x) {
      const parts = [x.coachNumber, x.storeName].filter(Boolean);
      return {
        id: x.id,
        label: parts.length
          ? `${x.fullName} (${parts.join(" — ")})`
          : x.fullName,
      };
    },
    searchCoachCandidate({ action, searchQuery, callback }) {
      const self = this;
      if (action !== ASYNC_SEARCH) return;

      self.$store
        .dispatch("apis/get", {
          url: `/apps-member/coach-candidate`,
          params: { search: searchQuery, pageNumber: 1, pageSize: 20 },
        })
        .then((response) => {
          if (response.error) callback(new Error(response.message));
          else
            callback(null, response.data.data.map(self.coachCandidateOption));
        });
    },
    submitLinkCoach() {
      const self = this;
      if (self.modalCoach.item == null || self.modalCoach.coachIds.length === 0)
        return;

      self.modalCoach.saving = true;
      self.$store
        .dispatch("apis/post", {
          url: `/apps-member/${self.modalCoach.item.id}/link-coach`,
          params: { coachIds: self.modalCoach.coachIds },
        })
        .then((response) => {
          if (response.error) {
            self.$message.error({ zIndex: 10000, message: response.message });
          } else {
            self.$message.success({ zIndex: 10000, message: response.message });
            self.modalCoach.show = false;
            self.getAll();
          }
        })
        .finally(() => (self.modalCoach.saving = false));
    },
    // --- Langkah 2: aktivasi — pilih member + auto-tambah produk biaya
    // pendaftaran ke keranjang, lalu buka /transaction/pos. ---
    activate(item) {
      const self = this;
      if (self.store == null) return;
      if (!item.hasCard) {
        self.$message.warning({
          zIndex: 10000,
          message: "Assign kartu member terlebih dahulu sebelum aktivasi.",
        });
        return;
      }

      self.converting = true;

      self.$store.dispatch(SET_MEMBER, {
        id: item.id,
        fullName: item.fullName,
        membershipNumber: item.membershipNumber,
        email: item.email,
        phone: item.phone,
      });

      self.$store
        .dispatch("apis/get", {
          url: `/store/${self.store.id}/product`,
          params: {
            isRegistrationFee: true,
            actived: true,
            pageNumber: 1,
            pageSize: 1,
          },
        })
        .then((response) => {
          const rows =
            response && !response.error && response.data
              ? response.data.data
              : [];

          if (rows && rows.length) {
            const fee = rows[0];
            self.$store.dispatch(SET_CART, [
              {
                product: fee,
                quantity: 1,
                discount: 0,
                price: fee.regularPrice,
                coachId: null,
              },
            ]);
          } else {
            self.$message.warning({
              zIndex: 10000,
              message:
                "Produk biaya pendaftaran belum diatur. Tambahkan produk di backoffice & tandai sebagai biaya pendaftaran, atau tambahkan manual di POS.",
            });
          }

          self.$router.push({ name: "pos" });
        })
        .finally(() => (self.converting = false));
    },
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps);
    },
    onPerPageChange(params) {
      this.updateParams({ pageSize: params, pageNumber: 1 });
      this.getAll();
    },
    onPageChange(params) {
      this.updateParams({ pageNumber: params });
      this.getAll();
    },
    onSearch(params) {
      this.updateParams({ fullName: params, pageNumber: 1 });
      this.getAll();
    },
    onFilter() {
      this.updateParams({ pageNumber: 1 });
      this.getAll();
    },
  },
};
</script>
